import React, { Component } from 'react';
import axios from 'axios';
import moment from "moment";
import { APIURL } from '../../config';
import { Card, CardContent, TextField, InputAdornment, Box, CircularProgress, Typography, Chip, Button, Dialog, DialogActions, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TaskForm from '../../components/forms/TaskForm/TaskForm';

class HouseholdTasks extends Component {
    state = {
        searchTerm: '',
        tasks: [],
        loading: true,
        householdMembers: [],
        projects: [],
        selectedTab: 0,
        tabs: ['Ongoing', 'Completed'],
        onGoingTasks: [],
        completedTasks: [],
        showNewTask: false,
        employees: { list: [] },
        locations: { list: [] },
        tasks_data: { taskType: [] },
        projects_data: { list: [] },
    }

    async componentDidMount() {
        await this.fetchData();
        await this.fetchDropdownData();
    }

    async componentDidUpdate(prevProps) {
        if (prevProps.household_id !== this.props.household_id) {
            await this.fetchData();
        }
    }

    async fetchDropdownData() {
        try {
            const [employeesRes, locationsRes, projectsRes, taskTypeRes] = await Promise.all([
                axios.get(`${APIURL}/dropdowns/dropdownType/employeeslist`, {
                    params: { comp_id: this.props.user?.comp_id }
                }),
                axios.get(`${APIURL}/dropdowns/dropdownType/locationslist`, {
                    params: { comp_id: this.props.user?.comp_id }
                }),
                axios.get(`${APIURL}/dropdowns/dropdownType/projectslist`, {
                    params: { comp_id: this.props.user?.comp_id }
                }),
                axios.get(`${APIURL}/dropdowns/dropdownType/taskType`, {
                    params: { comp_id: this.props.user?.comp_id }
                })
            ]);

            this.setState({
                employees: { list: employeesRes.data },
                locations: { list: locationsRes.data },
                projects_data: { list: projectsRes.data },
                tasks_data: { taskType: taskTypeRes.data }
            });
        } catch (error) {
            console.error('Error fetching dropdown data:', error);
        }
    }

    async fetchData() {
        if (!this.props.household_id) {
            this.setState({ loading: false, tasks: [], householdMembers: [], projects: [] });
            return;
        }

        this.setState({ loading: true });

        try {
            // First, fetch all household members (customers only)
            const membersResponse = await axios.get(`${APIURL}/households/members`, {
                params: { household_id: this.props.household_id }
            });
            const members = membersResponse.data || [];
            
            // Filter to only customers (not contacts)
            const customerMembers = members.filter(m => m.customer_id !== null);
            
            if (customerMembers.length === 0) {
                this.setState({ loading: false, tasks: [], householdMembers: [], projects: [] });
                return;
            }

            // Fetch projects for all customers in parallel
            const projectPromises = customerMembers.map(member =>
                axios.get(`${APIURL}/project`, { params: { cus_id: member.customer_id } })
                    .then(response => ({
                        customer_id: member.customer_id,
                        customer_name: member.cus_name,
                        projects: response.data || []
                    }))
                    .catch(error => {
                        console.error(`Error fetching projects for customer ${member.customer_id}:`, error);
                        return { customer_id: member.customer_id, customer_name: member.cus_name, projects: [] };
                    })
            );

            const projectsByCustomer = await Promise.all(projectPromises);
            
            // Flatten all projects and add customer info
            const allProjects = projectsByCustomer.flatMap(item =>
                item.projects.map(project => ({
                    ...project,
                    customer_id: item.customer_id,
                    customer_name: item.customer_name
                }))
            );

            if (allProjects.length === 0) {
                this.setState({ loading: false, tasks: [], householdMembers: customerMembers, projects: [] });
                return;
            }

            // Fetch tasks for all projects in parallel
            const taskPromises = allProjects.map(project =>
                axios.get(`${APIURL}/task`, { params: { pro_id: project.pro_id } })
                    .then(response => ({
                        project_id: project.pro_id,
                        project_name: project.pro_title,
                        customer_id: project.customer_id,
                        customer_name: project.customer_name,
                        tasks: response.data || []
                    }))
                    .catch(error => {
                        console.error(`Error fetching tasks for project ${project.pro_id}:`, error);
                        return {
                            project_id: project.pro_id,
                            project_name: project.pro_name,
                            customer_id: project.customer_id,
                            customer_name: project.customer_name,
                            tasks: []
                        };
                    })
            );

            const tasksByProject = await Promise.all(taskPromises);
            
            // Flatten all tasks and add project/customer info to each
            const allTasks = tasksByProject.flatMap(item =>
                item.tasks.map(task => ({
                    ...task,
                    project_id: item.project_id,
                    project_name: item.project_name,
                    customer_id: item.customer_id,
                    customer_name: item.customer_name
                }))
            );

            this.setState({
                tasks: allTasks,
                householdMembers: customerMembers,
                projects: allProjects,
                loading: false
            }, () => {
                this.splitTasks();
            });
        } catch (error) {
            console.error('Error fetching household tasks:', error);
            this.setState({ loading: false, tasks: [], householdMembers: [], projects: [] });
        }
    }

    handleSearchChange = (event) => {
        this.setState({ searchTerm: event.target.value });
    }

    selectTab = (tabIndex) => {
        this.setState({ selectedTab: tabIndex });
    }

    splitTasks = () => {
        const { tasks } = this.state;
        
        let onGoing = tasks.filter((task) => {
            const endTime = moment(task.tas_end_time);
            const startTime = moment(task.tas_start_time);
            const today = moment();
            
            // A task is ongoing if:
            // 1. End time is today or in the future, OR
            // 2. Task has no valid dates (null/undefined start or end times), OR
            // 3. Start time is invalid (before 1900)
            const hasValidEndTime = task.tas_end_time && endTime.isValid();
            const hasValidStartTime = task.tas_start_time && startTime.isValid();
            const endTimeIsAfterToday = hasValidEndTime && endTime.isSameOrAfter(today, 'day');
            const hasInvalidDates = !hasValidStartTime || !hasValidEndTime || startTime.isSameOrBefore("1900-01-01");
            
            return endTimeIsAfterToday || hasInvalidDates;
        });
        
        let completed = tasks.filter((task) => {
            const endTime = moment(task.tas_end_time);
            const startTime = moment(task.tas_start_time);
            const today = moment();
            
            // A task is completed if:
            // 1. Has valid start and end times, AND
            // 2. Start time is after 1900, AND
            // 3. End time is before today
            const hasValidEndTime = task.tas_end_time && endTime.isValid();
            const hasValidStartTime = task.tas_start_time && startTime.isValid();
            const endTimeIsBeforeToday = hasValidEndTime && endTime.isBefore(today, 'day');
            const startTimeIsAfter1900 = hasValidStartTime && startTime.isAfter("1900-01-01");
            
            return hasValidStartTime && hasValidEndTime && startTimeIsAfter1900 && endTimeIsBeforeToday;
        });

        this.setState({ onGoingTasks: onGoing, completedTasks: completed });
    }

    getFilteredTasks = () => {
        const { selectedTab, onGoingTasks, completedTasks, searchTerm } = this.state;
        const tasksToFilter = selectedTab === 0 ? onGoingTasks : completedTasks;
        
        if (!searchTerm.trim()) return tasksToFilter;
        
        const lowerSearch = searchTerm.toLowerCase();
        return tasksToFilter.filter(task =>
            task.tas_title?.toLowerCase().includes(lowerSearch) ||
            task.tas_description?.toLowerCase().includes(lowerSearch) ||
            task.customer_name?.toLowerCase().includes(lowerSearch) ||
            task.project_name?.toLowerCase().includes(lowerSearch)
        );
    }

    getTaskStatusLabel = (status) => {
        switch (status) {
            case 0: return 'Incomplete';
            case 1: return 'Completed';
            case 2: return 'Deleted';
            default: return 'Unknown';
        }
    }

    getTaskStatusColor = (status) => {
        switch (status) {
            case 0: return 'default';
            case 1: return 'primary';
            case 2: return 'error';
            default: return 'default';
        }
    }

    render() {
        const { loading, searchTerm, selectedTab, tabs, onGoingTasks, completedTasks } = this.state;
        const filteredTasks = this.getFilteredTasks();

        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                    <CircularProgress />
                </Box>
            );
        }

        return (
            <div style={{ padding: '20px' }}>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Search tasks..."
                        value={searchTerm}
                        onChange={this.handleSearchChange}
                        style={{ flex: 1 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={() => this.setState({ showNewTask: true })}
                    >
                        New Task
                    </Button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                    <div 
                        style={{
                            border: '1px solid #99BDF3',
                            borderRadius: '15px',
                            padding: '8px 20px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            background: tabs[selectedTab] === 'Ongoing' ? '#DBE9FF' : 'white',
                            color: tabs[selectedTab] === 'Ongoing' ? '#000000' : '#737373',
                            fontSize: '15px',
                            transition: 'all 0.2s'
                        }}
                        onClick={() => this.selectTab(0)}
                    >
                        <div>Ongoing</div>
                        {tabs[selectedTab] === 'Ongoing' && (
                            <div style={{
                                width: '20px',
                                height: '20px',
                                background: '#9FC4FE',
                                fontSize: '12px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                borderRadius: '16px'
                            }}>
                                {onGoingTasks.length}
                            </div>
                        )}
                    </div>
                    <div 
                        style={{
                            border: '1px solid #99BDF3',
                            borderRadius: '15px',
                            padding: '8px 20px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            background: tabs[selectedTab] === 'Completed' ? '#DBE9FF' : 'white',
                            color: tabs[selectedTab] === 'Completed' ? '#000000' : '#737373',
                            fontSize: '15px',
                            transition: 'all 0.2s'
                        }}
                        onClick={() => this.selectTab(1)}
                    >
                        <div>Completed</div>
                        {tabs[selectedTab] === 'Completed' && (
                            <div style={{
                                width: '20px',
                                height: '20px',
                                background: '#9FC4FE',
                                fontSize: '12px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                borderRadius: '16px'
                            }}>
                                {completedTasks.length}
                            </div>
                        )}
                    </div>
                </div>

                {filteredTasks.length === 0 ? (
                    <Box sx={{ textAlign: 'center', mt: 5 }}>
                        <Typography variant="h6" color="textSecondary">
                            No tasks found for this household
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                            {this.state.householdMembers.length === 0
                                ? 'Add customers to this household to see their tasks.'
                                : this.state.projects.length === 0
                                ? 'The customers in this household do not have any projects yet.'
                                : 'The projects in this household do not have any tasks yet.'}
                        </Typography>
                    </Box>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
                        {filteredTasks.map(task => (
                            <Card
                                key={task.tas_id}
                                onClick={() => this.props.history.push({
                                    pathname: '/projects',
                                    projectsProps: { pro_id: task.project_id, cus_id: task.customer_id, tab: 1 }
                                })}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        this.props.history.push({
                                            pathname: '/projects',
                                            projectsProps: { pro_id: task.project_id, cus_id: task.customer_id, tab: 1 }
                                        });
                                    }
                                }}
                                role="link"
                                tabIndex={0}
                                style={{
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    height: '100%',
                                    minHeight: '220px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    backgroundColor: '#d4f1f9',
                                    cursor: 'pointer'
                                }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-4px)';
                                        e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.1)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                    }}
                                >
                                    <CardContent style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                            <Typography variant="h6" component="div" style={{ fontWeight: 600, color: '#257bcd' }}>
                                                {task.tas_title}
                                            </Typography>
                                            <Chip
                                                label={this.getTaskStatusLabel(task.tas_is_active)}
                                                size="small"
                                                color={this.getTaskStatusColor(task.tas_is_active)}
                                            />
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '8px' }}>
                                            <PersonIcon fontSize="small" style={{ color: '#666' }} />
                                            <Typography variant="body2" color="textSecondary">
                                                {task.customer_name}
                                            </Typography>
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '8px' }}>
                                            <AssignmentIcon fontSize="small" style={{ color: '#666' }} />
                                            <Typography variant="body2" color="textSecondary">
                                                {task.project_name}
                                            </Typography>
                                        </div>

                                        {task.tas_description && (
                                            <Typography variant="body2" color="textSecondary" style={{ marginBottom: '12px' }}>
                                                {task.tas_description.length > 100
                                                    ? `${task.tas_description.substring(0, 100)}...`
                                                    : task.tas_description}
                                            </Typography>
                                        )}

                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '12px' }}>
                                            <Typography variant="caption" color="textSecondary">
                                                {task.tas_start_time
                                                    ? `Starts: ${moment(task.tas_start_time).format('MMM DD, YYYY h:mm A')}`
                                                    : 'No start time'}
                                            </Typography>
                                        </div>

                                        {task.tas_end_time && (
                                            <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
                                                {`Ends: ${moment(task.tas_end_time).format('MMM DD, YYYY h:mm A')}`}
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>

                        ))}
                    </div>
                )}

                <Dialog 
                    fullWidth={true} 
                    maxWidth={'sm'} 
                    open={this.state.showNewTask} 
                    onClose={() => this.setState({ showNewTask: false })}
                >
                    <DialogActions>
                        <h4 style={{ marginLeft: '20px', marginTop: '10px' }}>New Task</h4>
                        <hr style={{ marginBottom: '10px', marginTop: '10px', backgroundColor: '#e2ecfa' }}></hr>
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            sx={{ p: 0.25 }}
                            onClick={() => this.setState({ showNewTask: false })}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogActions>
                    <DialogContent sx={{ paddingTop: '0px' }}>
                        <TaskForm
                            {...this.props}
                            empList={this.state.employees.list}
                            locList={this.state.locations.list}
                            projList={this.state.projects_data.list}
                            taskType={this.state.tasks_data.taskType}
                            comp_id={this.props.user?.comp_id}
                            customerPage={false}
                            cus_id={null}
                            household_id={this.props.household_id}
                            onSubmit={() => {
                                this.setState({ showNewTask: false });
                                this.fetchData();
                            }}
                        />
                    </DialogContent>
                </Dialog>
            </div>
        );
    }
}

export default HouseholdTasks;
